import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Grid, Button, TextField,
  Divider, Paper, createTheme, ThemeProvider, CssBaseline,
  RadioGroup, FormControlLabel, Radio, FormControl, Snackbar
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import cart from '../Cart';
import User from '../data/User';
import API_URL from '../apiConfig';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';

const defaultTheme = createTheme({
  palette: {
    primary: { main: '#e88ba9' },
    background: { default: '#fffafb' },
    text: { primary: '#5c434a', secondary: '#8c7077' },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
    h3: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 800 },
    h4: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 700 },
    h5: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 600 },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '& fieldset': { borderColor: 'rgba(232,139,169,0.3)' },
            '&:hover fieldset': { borderColor: '#e88ba9' },
            '&.Mui-focused fieldset': { borderColor: '#e88ba9', borderWidth: '2px' },
          }
        }
      }
    }
  }
});

const CompleteButton = styled(Button)({
  background: '#e88ba9',
  borderRadius: '30px',
  color: 'white',
  padding: '16px 24px',
  boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  fontSize: '1.1rem',
  width: '100%',
  transition: 'all 0.3s ease',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)', background: '#d46a89' },
});

const StepContainer = styled(Paper)({
  borderRadius: '24px',
  border: '1px solid #fcf4f4',
  boxShadow: '0 10px 30px rgba(232, 139, 169, 0.08)',
  padding: '32px',
  background: '#fff',
  marginBottom: '24px'
});

const SummaryBox = styled(Paper)({
  borderRadius: '24px',
  border: '1px solid #e88ba9',
  boxShadow: '0 10px 40px rgba(232, 139, 169, 0.15)',
  padding: '32px',
  background: 'linear-gradient(135deg, #fffafb 0%, #ffedf4 100%)',
  position: 'sticky',
  top: '120px'
});

const parsePrice = (priceStr) => {
  if (!priceStr) return 0;
  const match = priceStr.toString().replace(/,/g, '').match(/\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
};

const Checkout = () => {
  const [items, setItems] = useState([]);
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [details, setDetails] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    date: ''
  });
  const [snack, setSnack] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!User.instance.getToken()) {
      navigate('/login?redirect=checkout');
      return;
    }
    const cartItems = cart.getItems();
    if (cartItems.length === 0) {
      navigate('/cart');
    }
    setItems(cartItems);
  }, [navigate]);

  const handleInputChange = (e) => {
    setDetails({ ...details, [e.target.name]: e.target.value });
  };

  const subtotal = items.reduce((total, item) => total + (parsePrice(item.price) * item.quantity), 0);
  const deliveryFee = deliveryMethod === 'delivery' ? 500 : 0;
  const total = subtotal + deliveryFee;

  const handlePlaceOrder = async () => {
    // Validate
    if (!details.fullName || !details.phone || (deliveryMethod === 'delivery' && !details.address)) {
      setSnack('Please fill in all required delivery details.');
      return;
    }

    const fullDeliveryAddress = deliveryMethod === 'delivery'
      ? `${details.address}, ${details.city} | COD`
      : `Store Pickup | ${details.date}`;

    try {
      const response = await fetch(`${API_URL}/api/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          items: items,
          deliveryAddress: fullDeliveryAddress,
          totalPrice: `LKR.${total.toFixed(2)}`
        })
      });

      if (response.ok) {
        cart.clearCart();
        navigate("/confirm");
      } else {
        setSnack("Session expired. Please log in to complete your order.");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (error) {
      console.error("Order error:", error);
      setSnack("Network error. Please try again.");
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ background: '#fffafb', minHeight: '100vh', pt: '100px', pb: 8 }}>
        <CssBaseline />
        <Container maxWidth="lg">

          <Typography variant="h3" component="h1" sx={{ textAlign: 'center', mb: 6 }}>
            Checkout
          </Typography>

          <Grid container spacing={4}>
            {/* Left Column: Forms */}
            <Grid item xs={12} md={7}>

              {/* Delivery Method Toggle */}
              <StepContainer elevation={0}>
                <Typography variant="h5" sx={{ mb: 3 }}>1. Delivery Method</Typography>
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    row
                    value={deliveryMethod}
                    onChange={(e) => setDeliveryMethod(e.target.value)}
                    sx={{ display: 'flex', gap: 2 }}
                  >
                    <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: '16px', borderColor: deliveryMethod === 'delivery' ? '#e88ba9' : 'rgba(0,0,0,0.12)', bgcolor: deliveryMethod === 'delivery' ? '#fffafb' : 'transparent', cursor: 'pointer' }} onClick={() => setDeliveryMethod('delivery')}>
                      <FormControlLabel value="delivery" control={<Radio sx={{ color: '#e88ba9', '&.Mui-checked': { color: '#e88ba9' } }} />} label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalShippingOutlinedIcon sx={{ color: deliveryMethod === 'delivery' ? '#e88ba9' : '#8c7077' }} />
                          <Typography sx={{ fontWeight: deliveryMethod === 'delivery' ? 700 : 500, color: '#5c434a' }}>Home Delivery</Typography>
                        </Box>
                      } sx={{ m: 0, width: '100%' }} />
                    </Paper>
                    <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: '16px', borderColor: deliveryMethod === 'pickup' ? '#e88ba9' : 'rgba(0,0,0,0.12)', bgcolor: deliveryMethod === 'pickup' ? '#fffafb' : 'transparent', cursor: 'pointer' }} onClick={() => setDeliveryMethod('pickup')}>
                      <FormControlLabel value="pickup" control={<Radio sx={{ color: '#e88ba9', '&.Mui-checked': { color: '#e88ba9' } }} />} label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StorefrontOutlinedIcon sx={{ color: deliveryMethod === 'pickup' ? '#e88ba9' : '#8c7077' }} />
                          <Typography sx={{ fontWeight: deliveryMethod === 'pickup' ? 700 : 500, color: '#5c434a' }}>Store Pickup</Typography>
                        </Box>
                      } sx={{ m: 0, width: '100%' }} />
                    </Paper>
                  </RadioGroup>
                </FormControl>
              </StepContainer>

              {/* Contact Details */}
              <StepContainer elevation={0}>
                <Typography variant="h5" sx={{ mb: 3 }}>2. Contact Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Full Name" name="fullName" value={details.fullName} onChange={handleInputChange} required />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Phone Number" name="phone" value={details.phone} onChange={handleInputChange} required />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Email Address" name="email" value={details.email} onChange={handleInputChange} />
                  </Grid>
                </Grid>
              </StepContainer>

              {/* Shipping Details */}
              {deliveryMethod === 'delivery' && (
                <StepContainer elevation={0}>
                  <Typography variant="h5" sx={{ mb: 3 }}>3. Shipping Address</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Street Address" name="address" value={details.address} onChange={handleInputChange} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="City" name="city" value={details.city} onChange={handleInputChange} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Requested Delivery Date" name="date" type="date" InputLabelProps={{ shrink: true }} value={details.date} onChange={handleInputChange} />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#fcf4f4', borderRadius: '12px' }}>
                    <Typography sx={{ color: '#d46a89', fontWeight: 600, fontSize: '0.9rem' }}>Payment is Cash on Delivery (COD) only.</Typography>
                  </Box>
                </StepContainer>
              )}

            </Grid>

            {/* Right Column: Order Summary */}
            <Grid item xs={12} md={5}>
              <SummaryBox elevation={0}>
                <Typography variant="h4" sx={{ mb: 4 }}>
                  Order Summary
                </Typography>

                {/* Items List Mini */}
                <Box sx={{ mb: 3, maxHeight: '250px', overflowY: 'auto', pr: 1 }}>
                  {items.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box component="img" src={item.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=100'} sx={{ width: 60, height: 60, borderRadius: '8px', objectFit: 'cover' }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: '#5c434a', fontSize: '0.9rem', lineHeight: 1.2 }}>{item.name}</Typography>
                        <Typography sx={{ color: '#8c7077', fontSize: '0.85rem' }}>Qty: {item.quantity}</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 600, color: '#d46a89' }}>LKR.{(parsePrice(item.price) * item.quantity).toFixed(2)}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ borderColor: 'rgba(232, 139, 169, 0.2)', mb: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography sx={{ color: '#8c7077', fontWeight: 500 }}>Subtotal</Typography>
                  <Typography sx={{ color: '#5c434a', fontWeight: 700 }}>LKR.{subtotal.toFixed(2)}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography sx={{ color: '#8c7077', fontWeight: 500 }}>{deliveryMethod === 'delivery' ? 'Delivery Fee' : 'Store Pickup'}</Typography>
                  <Typography sx={{ color: '#5c434a', fontWeight: 700 }}>{deliveryMethod === 'delivery' ? `LKR.${deliveryFee.toFixed(2)}` : 'FREE'}</Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(232, 139, 169, 0.2)', mb: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 4 }}>
                  <Typography variant="h5" sx={{ color: '#5c434a', fontWeight: 800 }}>Total</Typography>
                  <Typography variant="h4" sx={{ color: '#d46a89' }}>LKR.{total.toFixed(2)}</Typography>
                </Box>

                <CompleteButton onClick={handlePlaceOrder}>
                  Confirm Order
                </CompleteButton>

                <Typography sx={{ textAlign: 'center', mt: 2, color: '#8c7077', fontSize: '0.8rem' }}>
                  By placing your order, you agree to our Terms of Service and Privacy Policy. Payment will be collected upon delivery/pickup.
                </Typography>
              </SummaryBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={snack !== ''}
        message={snack}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{ sx: { background: '#9b2226', color: '#fff', fontWeight: 'bold' } }}
      />
    </ThemeProvider>
  );
};

export default Checkout;
